import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { db } from "../firebase";
import { withTimeout } from "../utils/promiseTimeout";
import { useAuth } from "./useAuth";

const AdminAlertCounterContext = createContext(null);
const ADMIN_ROUTES = new Set([
  "/admin",
  "/appManage",
  "/helpManage",
  "/order",
  "/pin",
  "/pinDisney",
]);

function sanitizeCount(value) {
  return Math.max(0, Number(value || 0));
}

async function loadFallbackCounts() {
  const orders = collection(db, "orders");
  const [waitingSnapshot, cancellationSnapshot] = await withTimeout(
    Promise.all([
      getCountFromServer(query(orders, where("status", "==", "waiting"))),
      getDocs(query(orders, where("cancelRequest.status", "==", "pending"))),
    ]),
    "โหลดจำนวนแจ้งเตือนใช้เวลานานเกินไป",
    10000,
  );

  const waitingCancellationCount = cancellationSnapshot.docs.filter(
    (orderDoc) => orderDoc.data()?.status === "waiting",
  ).length;

  return {
    waitingOrderCount: sanitizeCount(
      waitingSnapshot.data().count - waitingCancellationCount,
    ),
    cancelRequestCount: sanitizeCount(cancellationSnapshot.size),
  };
}

export function AdminAlertCounterProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const isAdminRoute =
    ADMIN_ROUTES.has(location.pathname) ||
    location.pathname.startsWith("/admin/product/");
  const [counts, setCounts] = useState({
    waitingOrderCount: 0,
    cancelRequestCount: 0,
  });
  const [source, setSource] = useState("idle");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !isAdminRoute) {
      setSource("idle");
      setError(null);
      return undefined;
    }

    let disposed = false;
    let fallbackStarted = false;
    const startedAt = performance.now();
    const slowTimer = window.setTimeout(() => {
      console.warn("[performance] adminAlertCounters.slow", {
        durationMs: 10000,
      });
    }, 10000);

    const fallback = async (reason) => {
      if (fallbackStarted || disposed) return;
      fallbackStarted = true;
      try {
        const fallbackCounts = await loadFallbackCounts();
        if (disposed) return;
        setCounts(fallbackCounts);
        setSource("fallback");
        console.warn("Admin alert counters using aggregate fallback:", reason);
      } catch (fallbackError) {
        if (disposed) return;
        setError(fallbackError);
        setSource("error");
        console.error("Admin alert counter fallback failed:", fallbackError);
      }
    };

    const unsubscribe = onSnapshot(
      doc(db, "adminCounters", "orderAlerts"),
      (snapshot) => {
        window.clearTimeout(slowTimer);
        if (disposed) return;

        if (!snapshot.exists()) {
          fallback("counter document does not exist");
          return;
        }

        const data = snapshot.data();
        if (Number(data.schemaVersion || 0) < 2) {
          fallback("counter schema is outdated");
          return;
        }
        setCounts({
          waitingOrderCount: sanitizeCount(data.waitingOrderCount),
          cancelRequestCount: sanitizeCount(data.cancelRequestCount),
        });
        setSource(snapshot.metadata.fromCache ? "cache" : "realtime");
        setError(null);
        console.info("[performance] adminAlertCounters.ready", {
          durationMs: Math.round(performance.now() - startedAt),
          fromCache: snapshot.metadata.fromCache,
        });
      },
      (listenerError) => {
        window.clearTimeout(slowTimer);
        if (disposed) return;
        setError(listenerError);
        console.error("Admin alert counter listener error:", listenerError);
        fallback(listenerError?.message || "listener failed");
      },
    );

    return () => {
      disposed = true;
      window.clearTimeout(slowTimer);
      unsubscribe();
    };
  }, [isAdminRoute, user]);

  const value = useMemo(
    () => ({ ...counts, source, error }),
    [counts, error, source],
  );

  return (
    <AdminAlertCounterContext.Provider value={value}>
      {children}
    </AdminAlertCounterContext.Provider>
  );
}

export function useAdminAlertCounters() {
  const context = useContext(AdminAlertCounterContext);
  if (!context) {
    throw new Error(
      "useAdminAlertCounters must be used inside AdminAlertCounterProvider",
    );
  }
  return context;
}
